import { memo, MouseEvent } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Pagination as SwanPagination } from '@vp/swan'

import { PerPage } from '~/app/features/Gallery/components/Subheader/PerPage'
import { useTranslations } from '~/app/features/Gallery/hooks/useTranslations'
import { PaginationButton } from '~/app/features/Gallery/components/Pagination/PaginationButton'
import { PaginationStep } from '~/app/features/Gallery/components/Pagination/PaginationStep'
import { PaginationSteps } from '~/app/features/Gallery/components/Pagination/PaginationSteps'
import { currentPageSelector, pageAndContentUpdate, numberOfPagesSelector, totalEntityCountSelector } from '~/app/store/paging'
import { galleryHeaderId } from '~/app/features/Gallery/components/Header/ActionHeader/constants'
import { handleBlur } from '~/app/utils/navigation'
import { scrollUpToElement } from '~/app/utils/scrollToElement'
import { ThunkDispatch } from '~/app/store/interface'
import type { GalleryPaginationHandlePageChangeFactory } from 'app/features/Gallery/components/Pagination/interface'

const MAX_PAGE_LINKS = 5
const STARTING_PAGE_NUMBER = 1

export const Pagination = memo(() => {
  const localize = useTranslations()
  const dispatch = useDispatch<ThunkDispatch>()

  const currentPage = useSelector(currentPageSelector)
  const numberOfPages = useSelector(numberOfPagesSelector)
  const resultCount = useSelector(totalEntityCountSelector)

  // localization
  const nextPageLabel = localize('PageNext')
  const previousPageLabel = localize('PagePrevious')
  const pageLinkPrefix = localize('PageTitle')
  const pageNumberLocalizer = (page: number): string => page.toString()
  const paginationLabel = localize('PaginationTitle')

  const shouldRenderMiddleSteps = numberOfPages > 2

  const handlePageChange = (page: number): void => {
    dispatch(pageAndContentUpdate(page))
    scrollUpToElement(galleryHeaderId)
  }

  const nextPage = currentPage === numberOfPages ? numberOfPages : currentPage + 1
  const handleNextPage = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault()

    handleBlur()

    handlePageChange(nextPage)
  }

  const handlePageChangeFactory: GalleryPaginationHandlePageChangeFactory =
    (page: number) =>
    (event: MouseEvent<HTMLAnchorElement>): void => {
      event.preventDefault()

      handleBlur()

      handlePageChange(page)
    }

  const prevPage = currentPage === STARTING_PAGE_NUMBER ? STARTING_PAGE_NUMBER : currentPage - 1
  const handlePreviousPage = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault()

    handleBlur()

    handlePageChange(prevPage)
  }

  return (
    <section className="pagination-container">
      {!!numberOfPages && (
        <SwanPagination accessibleText={paginationLabel} className="pagination-component">
          <PaginationButton
            accessibleText={previousPageLabel}
            disabled={numberOfPages === 0 || currentPage === STARTING_PAGE_NUMBER}
            pageNumber={prevPage}
            variant="previous"
            onClick={handlePreviousPage}
          />
          <PaginationStep
            accessibleText={`${pageLinkPrefix} ${STARTING_PAGE_NUMBER}`}
            current={STARTING_PAGE_NUMBER === currentPage}
            key={STARTING_PAGE_NUMBER}
            pageNumber={STARTING_PAGE_NUMBER}
            pageText={pageNumberLocalizer(STARTING_PAGE_NUMBER)}
            onClick={handlePageChangeFactory(STARTING_PAGE_NUMBER)}
          />
          {/* Remaining list of page links */}
          {shouldRenderMiddleSteps && (
            <PaginationSteps
              currentPage={currentPage}
              maxPageLinks={MAX_PAGE_LINKS}
              numberOfPages={numberOfPages}
              pageLinkPrefix={pageLinkPrefix}
              pageNumberLocalizer={pageNumberLocalizer}
              startingPageNumber={STARTING_PAGE_NUMBER}
              onClick={handlePageChangeFactory}
            />
          )}

          {numberOfPages > STARTING_PAGE_NUMBER && (
            <PaginationStep
              accessibleText={`${pageLinkPrefix} ${numberOfPages}`}
              current={numberOfPages === currentPage}
              key={numberOfPages}
              pageNumber={numberOfPages}
              pageText={pageNumberLocalizer(numberOfPages)}
              onClick={handlePageChangeFactory(numberOfPages)}
            />
          )}
          <PaginationButton
            accessibleText={nextPageLabel}
            disabled={numberOfPages === 0 || currentPage === numberOfPages}
            pageNumber={nextPage}
            variant="next"
            data-testid="next-page-button"
            onClick={handleNextPage}
          />
        </SwanPagination>
      )}
      {!!resultCount && <PerPage className="header-hierarchy-perpage" />}
    </section>
  )
})

Pagination.displayName = 'Pagination'
